"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown, ChevronRight, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { rijderHref } from "@/lib/secties";
import { FavorietKnop } from "@/components/favoriet-knop";
import { favorietSectie } from "@/lib/secties";
import type { Baan, ProgrammaKlasse } from "@/server/wedstrijden";

// Het programma per baan, op volgorde van de klok.
//
// Zo staat een bezoeker er ook: langs één baan, met de vraag wat daar zo komt.
// De indeling per sectie zegt op dat moment weinig, want Farm Stock en Grand
// National rijden door elkaar heen over de banen.
//
// Bij één baan vervalt de keuzebalk vanzelf; negen van de 48 wedstrijden in 2026
// hebben er maar één.
export function WedstrijdBanen({ banen }: { banen: Baan[] }) {
  const [actief, setActief] = useState(banen[0]?.nummer ?? 1);
  const [open, setOpen] = useState<string | null>(null);

  const baan = banen.find((b) => b.nummer === actief) ?? banen[0];
  if (!baan) return null;

  return (
    <div className="py-5">
      {banen.length > 1 && (
        <div className="mb-4 flex gap-2 overflow-x-auto px-4 pb-1">
          {banen.map((b) => (
            <button
              key={b.nummer}
              type="button"
              onClick={() => {
                setActief(b.nummer);
                setOpen(null);
              }}
              aria-pressed={b.nummer === actief}
              className={cn(
                "shrink-0 rounded-full px-4 py-2 font-display text-sm font-black uppercase tracking-wide transition-colors",
                b.nummer === actief
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-white/70 hover:text-white",
              )}
            >
              Baan {b.nummer}
            </button>
          ))}
        </div>
      )}

      <p className="mb-3 px-4 text-xs text-muted-foreground">
        {baan.klassen.length} {baan.klassen.length === 1 ? "onderdeel" : "onderdelen"} in
        startvolgorde
      </p>

      <ol className="space-y-2 px-4">
        {baan.klassen.map((k) => (
          <li key={k.key}>
            <Onderdeel
              klasse={k}
              open={open === k.key}
              onWissel={() => setOpen(open === k.key ? null : k.key)}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

function Onderdeel({
  klasse,
  open,
  onWissel,
}: {
  klasse: ProgrammaKlasse;
  open: boolean;
  onWissel: () => void;
}) {
  const aantal = klasse.deelnemers.length;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={onWissel}
        aria-expanded={open}
        disabled={aantal === 0}
        className="flex w-full items-center gap-3 px-3 py-3 text-left disabled:cursor-default"
      >
        {/* De tijd is waar je op zoekt als je langs de baan staat. */}
        <span className="flex w-14 shrink-0 flex-col items-center justify-center rounded-lg bg-muted py-1.5">
          {klasse.tijd ? (
            <span className="font-display text-base font-black leading-none tabular-nums text-primary">
              {klasse.tijd}
            </span>
          ) : (
            <Clock className="size-4 text-white/30" aria-hidden />
          )}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block font-display text-sm font-bold uppercase leading-snug tracking-wide text-white">
            {klasse.onderdeel}
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {aantal > 0
              ? `${aantal} ${aantal === 1 ? "deelnemer" : "deelnemers"}`
              : "Geen startlijst"}
          </span>
        </span>

        {aantal > 0 && (
          <ChevronDown
            className={cn(
              "size-5 shrink-0 text-primary transition-transform duration-200",
              open && "rotate-180",
            )}
            aria-hidden
          />
        )}
      </button>

      {open && aantal > 0 && (
        <ul className="divide-y divide-white/5 border-t border-white/5">
          {klasse.deelnemers.map((d, i) => {
            const href = rijderHref(klasse.sectieCode, d.deelnID);
            const inhoud = (
              <>
                <span className="w-8 shrink-0 text-center font-display text-sm font-black tabular-nums text-primary">
                  {d.startNR ?? "-"}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-white">{d.titel}</span>
                  {d.ondertitel && (
                    <span className="block truncate text-xs text-muted-foreground">{d.ondertitel}</span>
                  )}
                </span>
                {d.woonplaats && (
                  <span className="hidden shrink-0 text-xs text-muted-foreground xs:block">
                    {d.woonplaats}
                  </span>
                )}
              </>
            );
            return (
              <li key={`${d.deelnID ?? i}-${i}`} className="flex items-center">
                {href ? (
                  <Link
                    href={href}
                    className="flex min-w-0 flex-1 items-center gap-2 py-2.5 pl-2 transition-colors active:bg-white/5"
                  >
                    {inhoud}
                    <ChevronRight className="size-4 shrink-0 text-white/20" aria-hidden />
                  </Link>
                ) : (
                  <div className="flex min-w-0 flex-1 items-center gap-2 py-2.5 pl-2">{inhoud}</div>
                )}
                {d.deelnID != null && (
                  <FavorietKnop
                    favoriet={{
                      soort: "team",
                      sectie: favorietSectie(klasse.sectieCode),
                      id: d.deelnID,
                      naam: d.titel,
                    }}
                    lijst
                    smal
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
